import { Box, Grid, GridItem, HStack, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { PageHeader, SectionCard, DataTable, EmptyState, SegmentedProgressBar } from "../../../shared/ui";
import { AlertTriangle, Check, Clock, Users, Ban } from "../../../shared/icons";
import { formatarDataHora, parseDataISO } from "../../../shared/lib/date";
import { useAdminOverviewQuery } from "../hooks/useAdminOverviewQuery";
import OverviewMetric from "../components/OverviewMetric";

const PLANOS = [
  { key: "trial", label: "Em teste", tone: "warning", colorToken: "warning.500" },
  { key: "subscriber", label: "Assinantes", tone: "success", colorToken: "success.500" },
  { key: "free", label: "Plano grátis", tone: "brand", colorToken: "primary" },
  { key: "read_only", label: "Só leitura", tone: "danger", colorToken: "danger.500" },
];

// "aaaa-mm-dd" → "dd/mm/aaaa" (sem passar por Date, para não deslocar o dia pelo fuso)
const formatarData = (iso) => iso.split("-").reverse().join("/");
const formatarDiaMes = (iso) => formatarData(iso).slice(0, 5);

const plural = (n, singular, pluralForm) => `${n} ${n === 1 ? singular : pluralForm}`;

function Medico({ row }) {
  return (
    <Box minW={0}>
      <Text fontWeight="semibold" noOfLines={1}>
        {row.name || row.email}
      </Text>
      {row.name ? (
        <Text fontSize="xs" color="app-muted-fg" noOfLines={1}>
          {row.email}
        </Text>
      ) : null}
    </Box>
  );
}

const fimDoTeste = (row) => `em ${plural(row.days_left, "dia", "dias")}`;

const colunasTrials = [
  { key: "medico", label: "Médico", render: (row) => <Medico row={row} /> },
  {
    key: "trial_ends_on",
    label: "Fim do teste",
    render: (row) => (
      <>
        <Text>{formatarData(row.trial_ends_on)}</Text>
        <Text fontSize="xs" color="app-muted-fg">
          {fimDoTeste(row)}
        </Text>
      </>
    ),
  },
  { key: "workplaces", label: "Vínculos", align: "right" },
  { key: "shifts", label: "Plantões", align: "right" },
];

function TrialMobileRow(row) {
  return (
    <HStack justify="space-between" align="flex-start" py={3} spacing={3}>
      <Medico row={row} />
      <Box textAlign="right" flexShrink={0}>
        <Text fontSize="sm">{formatarData(row.trial_ends_on)}</Text>
        <Text fontSize="xs" color="app-muted-fg">
          {plural(row.workplaces, "vínculo", "vínculos")} · {plural(row.shifts, "plantão", "plantões")}
        </Text>
      </Box>
    </HStack>
  );
}

function CadastrosPorSemana({ semanas }) {
  const maior = Math.max(1, ...semanas.map((s) => s.count));
  return (
    <VStack align="stretch" spacing={2}>
      {semanas.map((s) => (
        <HStack key={s.week_start} spacing={3}>
          <Text fontSize="sm" color="app-muted-fg" w="44px" flexShrink={0}>
            {formatarDiaMes(s.week_start)}
          </Text>
          <Box flex={1} h="10px" bg="app-muted" borderRadius="full" overflow="hidden">
            {/* width inline: valor dinâmico geraria uma classe emotion nova por render */}
            <Box h="100%" bg="primary" borderRadius="full" style={{ width: `${(s.count / maior) * 100}%` }} />
          </Box>
          <Text fontSize="sm" fontWeight="semibold" w="28px" textAlign="right" flexShrink={0}>
            {s.count}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
}

export default function AdminOverviewPage() {
  const { data, isLoading, isError } = useAdminOverviewQuery();

  if (isError) {
    return (
      <>
        <PageHeader title="Visão geral" />
        <SectionCard>
          <EmptyState
            icon={AlertTriangle}
            title="Não foi possível carregar a visão geral."
            description="Tente novamente em alguns instantes."
          />
        </SectionCard>
      </>
    );
  }

  const desativadas = data?.deactivated_users ?? 0;

  return (
    <>
      <PageHeader
        title="Visão geral"
        description="Saúde da base de médicos: planos, ativação e testes perto do fim. Contas de administrador ficam fora dos números."
      />

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
        <OverviewMetric
          label="Médicos na base"
          value={data?.total_users}
          icon={Users}
          tone="brand"
          isLoading={isLoading}
          footnote={desativadas > 0 ? `${plural(desativadas, "conta desativada", "contas desativadas")} fora dos números` : undefined}
        />
        <OverviewMetric
          label="Ativados"
          value={data?.activated}
          icon={Check}
          tone="success"
          isLoading={isLoading}
          footnote="1+ vínculo e 3+ plantões"
        />
        <OverviewMetric
          label="Entraram nos últimos 7 dias"
          value={data?.active_last_7_days}
          icon={Clock}
          tone="teal"
          isLoading={isLoading}
          footnote="pelo último login registrado"
        />
        <OverviewMetric
          label="Sem nenhum plantão"
          value={data?.without_shifts}
          icon={Ban}
          tone="danger"
          isLoading={isLoading}
          footnote="cadastraram e não registraram plantão"
        />
      </SimpleGrid>

      <SectionCard title="Por plano" mb={6}>
        <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={4}>
          {PLANOS.map((p) => (
            <OverviewMetric key={p.key} label={p.label} value={data?.by_plan?.[p.key]} tone={p.tone} isLoading={isLoading} />
          ))}
        </SimpleGrid>
        {data ? (
          <SegmentedProgressBar
            showLegend={false}
            segments={PLANOS.map((p) => ({ label: p.label, value: data.by_plan[p.key], colorToken: p.colorToken }))}
          />
        ) : null}
        {data ? (
          <Text fontSize="sm" color="app-muted-fg" mt={4}>
            E-mail: {data.email_verified} verificados · {data.email_unverified} não verificados
          </Text>
        ) : null}
      </SectionCard>

      <SectionCard as="section" aria-label="Testes que vencem em 7 dias" title="Testes que vencem em 7 dias" mb={6}>
        <DataTable
          columns={colunasTrials}
          rows={(data?.expiring_trials ?? []).map((t) => ({ ...t, id: t.user_id }))}
          isLoading={isLoading}
          renderMobileRow={TrialMobileRow}
          emptyState={<EmptyState icon={Clock} title="Nenhum teste vence nos próximos 7 dias" />}
        />
      </SectionCard>

      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
        <GridItem minW={0}>
          <SectionCard as="section" aria-label="Cadastros por semana" title="Cadastros por semana">
            {data ? <CadastrosPorSemana semanas={data.signups_by_week} /> : null}
          </SectionCard>
        </GridItem>
        <GridItem minW={0}>
          <SectionCard as="section" aria-label="Últimos acessos" title="Últimos acessos">
            {data && data.recent_logins.length === 0 ? (
              <EmptyState icon={Users} title="Nenhum médico entrou ainda" />
            ) : (
              <VStack align="stretch" spacing={3}>
                {(data?.recent_logins ?? []).map((r) => (
                  <HStack key={r.user_id} justify="space-between" spacing={3}>
                    <Medico row={r} />
                    <Text fontSize="sm" color="app-muted-fg" flexShrink={0}>
                      {formatarDataHora(parseDataISO(r.last_login_at))}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            )}
          </SectionCard>
        </GridItem>
      </Grid>
    </>
  );
}
